import { type RefObject, useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { Layout } from 'react-grid-layout';
import {
  calculateItemHeightFromPixelHeight,
  getFilteredUtrsData,
  ROW_HEIGHT,
  ITEM_GAP,
  TABLE_ROW_HEIGHT,
  TABLE_HEADER_HEIGHT,
  MAX_VISIBLE_ROWS_NON_WRAPPED,
} from '../../../routes/custom-dashboard/utils';
import { GridDashboardItem, GridDashboardTableItem } from '../../../types/insight-custom-dashboard';
import { ChartUtrData } from '../../../routes/custom-dashboard/items/types';
import {
  getTableItems,
  type TableItemInfo,
  isDirectTable,
  isWrappedDirectTable,
} from '../utils/table-utils';

/** Grid height for non-wrapped table (max 5 visible rows). */
const calculateNonWrappedTableHeight = (
  tableItem: GridDashboardTableItem,
  utrsData: ChartUtrData[],
): number => {
  const { filteredUtrsData } = getFilteredUtrsData(utrsData, tableItem.variables);
  const utrData = filteredUtrsData[0];
  const tableData = (utrData?.utrvs ?? []).flatMap((utrv) => utrv.valueData?.table ?? []);

  const tableRowsCount = tableData.length === 0 ? 1 : Math.min(tableData.length, MAX_VISIBLE_ROWS_NON_WRAPPED);
  return Math.ceil((tableRowsCount * TABLE_ROW_HEIGHT + TABLE_HEADER_HEIGHT + ITEM_GAP) / (ROW_HEIGHT + ITEM_GAP));
};

/** Adjusts layout with measured table heights and shifts items below accordingly. */
const computeAdjustedLayout = (
  layout: Layout[],
  measuredHeights: Map<string, number>,
  directTableIds: Set<string>,
): Layout[] => {
  if (measuredHeights.size === 0 || directTableIds.size === 0) {
    return layout;
  }

  // Calculate height changes for direct tables
  const heightChanges: { itemId: string; originalY: number; originalH: number; newH: number; }[] =
    [];

  layout.forEach((item) => {
    if (directTableIds.has(item.i)) {
      const measuredH = measuredHeights.get(item.i);
      if (measuredH && measuredH !== item.h) {
        heightChanges.push({
          itemId: item.i,
          originalY: item.y,
          originalH: item.h,
          newH: measuredH,
        });
      }
    }
  });

  if (heightChanges.length === 0) {
    return layout;
  }

  // Sort by Y position (top to bottom) to process in order
  heightChanges.sort((a, b) => a.originalY - b.originalY);

  const offsetThresholds: { y: number; offset: number }[] = heightChanges.map((change) => ({
    y: change.originalY + change.originalH,
    offset: change.newH - change.originalH,
  }));

  // Apply adjustments to all items
  return layout.map((item) => {
    const change = heightChanges.find((c) => c.itemId === item.i);
    const newH = change?.newH;

    // Calculate cumulative offset from all tables whose bottom edge is at or above this item's Y
    const yOffset = offsetThresholds.reduce((sum, t) => item.y >= t.y ? sum + t.offset : sum, 0);

    if (newH) {
      return { ...item, h: change.newH, maxH: change.newH, minH: change.newH, y: item.y + yOffset };
    }

    if (yOffset !== 0) {
      return { ...item, y: item.y + yOffset };
    }

    return item;
  });
};

/**
 * Manages dynamic table heights in the dashboard grid layout.
 *
 * Workflow:
 * 1. Separates tables into wrapped (text-wrap enabled) and non-wrapped types
 * 2. Non-wrapped tables: height calculated directly from row count (max 5 visible)
 * 3. Wrapped tables: height measured from DOM via ResizeObserver after render
 * 4. Both heights combined to adjust layout, shifting items below tables accordingly
 *
 * Race condition handling: measurementVersionRef invalidates stale debounced callbacks
 * when data changes rapidly, ensuring only current measurements are applied.
 */
export const useDashboardTableHeight = ({
  gridItems,
  layout,
  dashboardRef,
  utrsData,
}: {
  gridItems: GridDashboardItem[];
  layout: Layout[];
  dashboardRef: RefObject<HTMLDivElement | null>;
  utrsData: ChartUtrData[];
}) => {
  const [measuredHeights, setMeasuredHeights] = useState<Map<string, number>>(() => new Map());
  const measurementVersionRef = useRef(0);

  const { directTableIds, wrappedTables, nonWrappedTables } = useMemo(() => {
    return gridItems.reduce(
      (acc, item) => {
        if (isDirectTable(item)) {
          acc.directTableIds.add(item._id);
          if (isWrappedDirectTable(item)) {
            acc.wrappedTables.push(item);
          } else {
            acc.nonWrappedTables.push(item);
          }
        }
        return acc;
      },
      {
        directTableIds: new Set<string>(),
        wrappedTables: [] as GridDashboardTableItem[],
        nonWrappedTables: [] as GridDashboardTableItem[],
      },
    );
  }, [gridItems]);

  const viewLayout = useMemo(() => {
    const combinedHeights = new Map<string, number>();
    nonWrappedTables.forEach((item) => {
      combinedHeights.set(item._id, calculateNonWrappedTableHeight(item, utrsData));
    });
    measuredHeights.forEach((height, id) => {
      combinedHeights.set(id, height);
    });
    return computeAdjustedLayout(layout, combinedHeights, directTableIds);
  }, [layout, nonWrappedTables, utrsData, measuredHeights, directTableIds]);

  const calculateTableHeight = useCallback((tableInfo: TableItemInfo): number => {
    const { element } = tableInfo;
    const rect = element.getBoundingClientRect();
    return calculateItemHeightFromPixelHeight(rect.height);
  }, []);

  const measureTables = useDebouncedCallback(
    (version: number) => {
      // Skip if this measurement is for an old version, mean this is stale
      if (version !== measurementVersionRef.current) {
        return;
      }

      if (!wrappedTables.length || !dashboardRef.current) {
        return;
      }

      const tableItems = getTableItems(dashboardRef, wrappedTables);

      // Only proceed if we found ALL expected wrapped tables in the DOM
      if (tableItems.length !== wrappedTables.length) {
        return;
      }

      const newHeights = new Map<string, number>();
      tableItems.forEach((tableInfo) => {
        const height = calculateTableHeight(tableInfo);
        newHeights.set(tableInfo.itemId, height);
      });

      // Update state only if heights changed
      setMeasuredHeights((prev) => {
        let hasChanges = prev.size !== newHeights.size;
        if (!hasChanges) {
          for (const [id, height] of newHeights) {
            if (prev.get(id) !== height) {
              hasChanges = true;
              break;
            }
          }
        }
        return hasChanges ? newHeights : prev;
      });
    },
    100,
    { leading: false, trailing: true },
  );

  useEffect(() => {
    if (!wrappedTables.length || !dashboardRef.current) {
      setMeasuredHeights(new Map());
      return;
    }

    // Increment version to invalidate any stale measurements
    measurementVersionRef.current += 1;
    const currentVersion = measurementVersionRef.current;
    setMeasuredHeights(new Map());

    const tableItems = getTableItems(dashboardRef, wrappedTables);
    if (tableItems.length === 0) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      measureTables(currentVersion);
    });

    tableItems.forEach((tableInfo) => resizeObserver.observe(tableInfo.element));

    // Initial measurement with delay to allow DOM to settle after data changes
    const timeoutId = setTimeout(() => {
      measureTables(currentVersion);
    }, 100);

    return () => {
      resizeObserver.disconnect();
      clearTimeout(timeoutId);
    };
  }, [wrappedTables, utrsData, measureTables, dashboardRef]);

  return { viewLayout };
};
